import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { batch, useDispatch, useSelector } from 'react-redux';
import { POPUP } from 'utils/constants/popup';
import { useTranslation } from 'utils/modifiedTranslation';
import { getUserCondition, getUserConditionProfile } from 'modules/user/utils';
import { SearchForm } from 'new-ui/Components/SearchForm/SearchForm';
import { GENERAL_DISEASE } from 'utils/constants/disease';
import app from 'new-ui/app';
import { getIsHCPPatient, mergeExtractedProfile } from 'modules/hcp/helpers';
import { GA_HCP } from 'modules/ga/hcp';
import { getGeneralDiseaseSubType } from 'modules/user/conditionProfileHelpers';
import { addCurrentUserPendingDocuments, updateCurrentUser } from 'modules/currentUser/actions';
import { savePatientPendingDocuments } from 'modules/hcp/actions/savePatientPendingDocuments';
import { saveHCPPatient } from 'modules/hcp/actions/saveHCPPatient';
import { useCurrentUserPublicMatches } from 'modules/hcp/useCurrentUserPublicMatches';
import Button from 'new-ui/Components/Button';
import { THEMES } from 'new-ui/constants';
import ArrowLeft from 'assets/icons/arrow_left_2.svg';
import { SUCCESS } from 'utils/constants/queryStatuses';
import { TRIAL_TYPES } from 'utils/constants/trial';
import { hideBackdrop, showBackdrop } from 'modules/backdrop/actions';
import { pushPopup } from 'modules/popup/actions';
import { AiAssistant } from './AiAssistant';
import styles from './checkCurrentUserTrialEligibility.module.css';

const BACKDROP_REASON = 'CheckCurrentUserTrialEligibility';

export const CheckCurrentUserTrialEligibility = ({ closePopup, trialType, nctNumber }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.currentUser.data);

  const { status: userMatchedItemFetchingStatus, data: matches } = useCurrentUserPublicMatches();

  const userMatchedItem = userMatchedItemFetchingStatus === SUCCESS
    ? matches[trialType === TRIAL_TYPES.TREATMENT ? 'treatments' : 'trials'].find((i) => i.nct_number === nctNumber)
    : null;

  const { missed_questions: itemMissedQuestions } = userMatchedItem ?? {};

  const missedQuestionsRef = useRef(itemMissedQuestions);

  useEffect(() => {
    if (!missedQuestionsRef.current && itemMissedQuestions) {
      missedQuestionsRef.current = itemMissedQuestions;
    }
  }, [itemMissedQuestions]);

  const isRegisteredPatient = getIsHCPPatient(user);

  const saveUser = useCallback((nextUser) => {
    if (!nextUser) {
      return;
    }
    batch(async () => {
      dispatch(updateCurrentUser({ user: nextUser, dirty: true }));

      if (!isRegisteredPatient) {
        return;
      }

      try {
        await dispatch(saveHCPPatient());
      } catch (ex) {
        console.error('saveHCPPatient ex', ex);
      }
    });
  }, [dispatch, isRegisteredPatient]);

  const onUploadedFilesChange = useCallback((files, { jobId, source }) => {
    if (!files?.length) {
      return;
    }
    batch(async () => {
      dispatch(addCurrentUserPendingDocuments([{ files, metadata: { jobId, source } }]));

      if (!isRegisteredPatient) {
        return;
      }

      dispatch(savePatientPendingDocuments())
        .catch((ex) => {
          console.error('savePatientPendingDocuments ex', ex);
        });
    });
  }, [dispatch, isRegisteredPatient]);

  const upload = (_, presentNgsNotFound = false) => {
    const condition = getUserCondition(user);
    const generalDiseaseSubType = condition === GENERAL_DISEASE
      ? getGeneralDiseaseSubType(getUserConditionProfile(user))
      : null;

    const getHandleProfile = (showPreloader = true) => async (nextUser) => {
      app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.UPLOAD_SUCCESS);

      if (showPreloader) {
        dispatch(showBackdrop({ reason: BACKDROP_REASON }));
      }

      try {
        const finalNextUser = await mergeExtractedProfile({ currentProfile: user, extractedProfile: nextUser });
        saveUser(finalNextUser);
      } finally {
        if (showPreloader) {
          dispatch(hideBackdrop({ reason: BACKDROP_REASON }));
        }
      }
    };

    app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.UPLOAD_FILES_CLICKED);
    dispatch(pushPopup({
      type: POPUP.CURRENT_USER_UPLOAD,
      data: {
        theme: 'middle',
        preventOverlayClose: true,
        condition,
        generalDiseaseSubType,
        onUploadedFilesChange,
        presentNgsNotFound,
        onSuccess: getHandleProfile(),
        onUpdate: getHandleProfile(false),
      },
    }));
  };

  const { current: missedQuestions } = missedQuestionsRef;
  if (!missedQuestions) {
    return null;
  }

  return (
    <div>
      <div className={styles.popupHeader}>
        <div className={styles.popupHeaderStartGroup}>
          <Button
            theme={THEMES.GREY}
            icon={ArrowLeft}
            title={t('general.back')}
            action={closePopup}
          />
        </div>
        <div className={styles.popupHeaderEndGroup}>
          <Button
            title={t('general.save')}
            theme={THEMES.ORANGE_FULL}
            action={closePopup}
          />
        </div>
      </div>
      <div className={styles.aiAssistant}>
        <AiAssistant uploadAction={upload} ngsAction={(e) => upload(e, true)} />
      </div>
      <div className={styles.intakeHolder}>
        <SearchForm
          id="checkTrialEligibility"
          indicationChangeDisabled={!!getUserCondition(user)}
          user={user}
          onAccordionRender={() => {
            const allQuestions = document.querySelectorAll('.intake-question');
            allQuestions.forEach(($q) => {
              const qid = $q.getAttribute('qid');
              if (!missedQuestions.length || !missedQuestions.includes(qid)) {
                return;
              }
              const isAnswered = Boolean($q.getAttribute('data-answered') === 'true');
              if (!isAnswered) {
                $q.classList.add('not-eligible');
              }
            });
          }}
          onChange={saveUser}
          onLoad={saveUser}
        />
      </div>
    </div>
  );
};

CheckCurrentUserTrialEligibility.propTypes = {
  closePopup: PropTypes.func.isRequired,
  nctNumber: PropTypes.string.isRequired,
  trialType: PropTypes.string,
};
